// Reactivity Created by PimiTree https://github.com/PimiTree/js-reactivity

const createRef = (value) => {
  return {
    refValue: value,
    stabeEffects: [],
    namedEffects: {},
    __raf: 0,

    /**
     * Setter that updates the reference value and schedules effect callbacks
     * @param {any} value - New value to set
     */
    set value(value) {
      this.refValue = value;
      cancelAnimationFrame(this.__raf);

      this.__raf = requestAnimationFrame(() => {
        this.callEffects();
      })
    },
    /**
     * Getter that returns the current reference value
     * @returns {any} The current value
     */
    get value() {
      return this.refValue;
    },
  }
}

const createShallowProxyRef = (value) => {
  return new function () {
    const refValue = value;
    this.stabeEffects = [];
    this.namedEffects = {};
    this.raf = 0;

    /**
     * Proxied object that tracks all property changes
     * @type {Proxy}
     */
    this.value = new Proxy(refValue, {

      /**
       * Trap for setting properties that triggers effect callbacks
       * @param {Object} target - The target object
       * @param {string|symbol} key - The property key
       * @param {any} value - The new property value
       * @param {Object} receiver - The proxy or object the property is being set on
       * @returns {boolean} Always returns true to indicate success
       */
      set: (target, key, value, receiver) => {
        Reflect.set(target, key, value, receiver)

        cancelAnimationFrame(this.raf);

        this.raf = requestAnimationFrame(() => {
          this.callEffects();
        })

        return true;
      },

      /**
       * Trap for getting properties
       * @param {Object} target - The target object
       * @param {string|symbol} key - The property key
       * @param {Object} receiver - The proxy or object the property is being accessed on
       * @returns {any} The property value
       */
      get(target, key, receiver) {
        return Reflect.get(target, key, receiver);
      }
    })
  }
}

const ref = (value, options) => {

  /**
   * The reference object to be returned
   * @type {RefObject}
   */
  let ref;


  if (typeof value === 'object' && !(value instanceof Date) && options?.type !== 'setter') {
    ref = createShallowProxyRef(value);
  } else {
    ref = createRef(value);
  }

  /**
   * Registers an effect callback to be called when the reference value changes
   * @param {EffectCallback|EffectCallback[]} effect - Single effect function or array of effect functions
   * @param {EffectOptions} [effectOptions] - Options for the effect
   */
  ref.effect = function (effect, effectOptions) {
    const totalEffects = this.__getNewEffects(effect);

    if (totalEffects.length === 0) return;

    let effectContainer;
    if (effectOptions?.name !== undefined) {
      if (this.namedEffects[effectOptions.name] === undefined) this.namedEffects[effectOptions.name] = [];

      effectContainer = this.namedEffects[effectOptions.name];
    } else {
      effectContainer = this.stabeEffects;
    }

    effectContainer.push(...totalEffects);

    if (effectOptions?.firstCall === undefined || effectOptions.firstCall === true) {
      totalEffects.forEach((func) => {
        func(this.value)
      })
    }
  }

  /**
   * Internal helper to filter out already registered effects
   * @private
   * @param {EffectCallback|EffectCallback[]} effect - Effect function or array of effect functions
   * @returns {EffectCallback[]} Array of effect functions that aren't already registered
   */
  ref.__getNewEffects = function (effect) {
    const inputEffects = Array.isArray(effect) ? effect : [effect];


    const newEffects = this.stabeEffects.length > 0
      ? inputEffects.filter((effect) => !this.stabeEffects.includes(effect))
      : inputEffects;

    const localNamedEffects = Object.values(this.namedEffects);
    const hasNamedEffects = localNamedEffects.some((list) => list.length > 0);
    const totalEffects = hasNamedEffects
      ? localNamedEffects.reduce((totalEffects, namedList) => {
        return totalEffects.filter((effect) => !namedList.includes(effect));

      }, newEffects)
      : newEffects;

    return totalEffects;
  }

  /**
   * Triggers all registered effect callbacks with the current value
   */
  ref.callEffects = function () {
    this.stabeEffects.forEach((effect) => {
      effect(this.value);
    });

    Object.values(this.namedEffects).forEach((namedEffect) => {
      namedEffect.forEach((effect) => {
        effect(this.value)
      })
    })
  }

  /**
   * Checks if an effect is already registered
   * @param {EffectCallback|EffectCallback[]} effect - Effect function or array of effect functions to check
   * @returns {boolean} True if all provided effects are already registered
   */
  ref.isEffectExist = function (effect) {
    return this.__getNewEffects(effect).length === 0
  }

  /**
   * Gets all names of registered named effect groups
   * @type {string[]}
   *
   */
  Object.defineProperty(ref, 'effectNames', {
    get: function () {
      /**
       * @this {RefObject}
       * @this.namedEffects {Object.<string, EffectCallback[]>}
       * @returns {string[]} Array of effect group names
       */
      return Object.keys(this.namedEffects);
    }
  })

  return ref;
}

const intersection = (arr1, arr2) => {
  return arr1.filter(Set.prototype.delete, new Set(arr2));
}

class SkinQuiz {
  constructor(props) {
    this.questions = props.questions;
    this.finalStep = props.finalStep;
    this.redirectMap = props.redirectMap;

    this.quizElement = document.querySelector('.skin-quiz');
    this.stepCounter = this.quizElement.querySelector('.skin-quiz__step-counter');
    this.stepCurrentEl = this.quizElement.querySelector('.skin-quiz__step-current');
    this.stepTotalEl = this.quizElement.querySelector('.skin-quiz__step-total');

    this.prevQuestion = null;
    this.finalStepSelected = null;
  }

  init = () => {
    this.createRegularQuestionElements();
    this.setButtonsEvents();
    this.setQuestionWrapperEvents();

    this.createCurrentQuestionRef();

    return this;
  }

  createCurrentQuestionRef = () => {
    this.currentQuestion = ref(0);
    this.currentQuestion.effect(this.renderCurrentQuestion);
  }

  createRegularQuestionElements = () => {
    this.questions.forEach((question) => {

      question.headerElement = document.createElement('div');
      question.headerElement.classList.add('skin-quiz__question-header', 'fade-in-KT');
      question.headerElement.textContent = question.header;

      question.answerWrapper = document.createElement('div');
      question.answerWrapper.classList.add('skin-quiz__answer-wrapper', 'fade-in-KT');

      question.answers.forEach((answer) => {
        answer.element = document.createElement('div');
        answer.element.classList.add('skin-quiz__answer');
        answer.element.answer = answer.text;
        answer.element.redirect = answer.redirect;
        answer.element.innerHTML = `
           <div class="skin-quiz__answer-circle"></div>
          <span>${answer.text}</span>
        `
        answer.element.chosen = false;
        question.answerWrapper.append(answer.element);
      })

      question.fragment = document.createDocumentFragment();
      question.fragment.append(question.headerElement, question.answerWrapper);
    })
  }

  setButtonsEvents = () => {
    this.prevButton = this.quizElement.querySelector('.skin-quiz__button-prev');
    this.prevButton.addEventListener('click', () => {
      this.prevQuestion = this.currentQuestion.value;

      if (this.prevQuestion === 2) {
        this.questionWrapper.innerHTML = '';
        this.finalStepSelected = null;
      }

      this.currentQuestion.value -= 1;
    });

    this.nextButton = this.quizElement.querySelector('.skin-quiz__button-next');
    this.nextButton.addEventListener('click', () => {

      if (this.currentQuestion.value === this.questions.length) {
        if (this.finalStepSelected) {
          window.location.href = this.finalStepSelected.redirect;
        }
        return;
      }

      const currentAnswer = this.questions[this.currentQuestion.value].answers.find((answer) => answer.chosen);

      if (typeof currentAnswer.redirect === "string" && this.currentQuestion.value === 0) {
        window.open(currentAnswer.redirect, '_blank');
        return;
      }

      if (currentAnswer.redirect === true && this.currentQuestion.value === 1) {
        const prevAnswer = this.questions[this.currentQuestion.value - 1].answers.find((answer) => answer.chosen);

        window.open(prevAnswer.redirectFallback, '_blank');
        return;
      }

      this.prevQuestion = this.currentQuestion.value;
      this.currentQuestion.value += 1;
    });
  }

  setQuestionWrapperEvents = () => {
    this.questionWrapper = this.quizElement.querySelector('.skin-quiz__question');
    this.questionWrapper.addEventListener('click', (e) => {
      const answerElement = e.target.closest('.skin-quiz__answer');

      if (!answerElement) return;

      if (this.currentQuestion.value === this.questions.length) {
        this.finalStepSelected = { redirect: answerElement.dataset.redirect };
        this.questionWrapper.querySelectorAll('.skin-quiz__answer').forEach((el) => el.classList.remove('active'));
        answerElement.classList.add('active');
        this.nextButtonAvailabilityControl();
        return;
      }

      this.questions[this.currentQuestion.value].answers.forEach((answer) => {
        if (answer.element === answerElement) {
          answer.chosen = true;
          answer.element.classList.add('active');
        } else {
          answer.chosen = false;
          answer.element.classList.remove('active');
        }
      })

      this.nextButtonAvailabilityControl();
    });
  }

  renderCurrentQuestion = () => {
    if (this.currentQuestion.value < this.questions.length) {
      this.renderRegularQuestion();
    } else {
      this.renderFinalStepQuestion();
    }

    this.bulletActivityControl();
    this.nextButtonAvailabilityControl();
    this.buttonsVisibilityControl();
  }

  bulletActivityControl = () => {
    const total = this.questions.length + 1;
    const current = this.currentQuestion.value + 1;
    if (this.stepCurrentEl) this.stepCurrentEl.textContent = current;
    if (this.stepTotalEl) this.stepTotalEl.textContent = total;
  }

  renderRegularQuestion = () => {
    if (this.prevQuestion != null && this.prevQuestion !== this.questions.length) {
      this.questions[this.prevQuestion].fragment.append(...this.questionWrapper.childNodes);
    }

    this.questionWrapper.append(this.questions[this.currentQuestion.value].fragment);
  }

  renderFinalStepQuestion = () => {
    this.questions[this.prevQuestion].fragment.append(...this.questionWrapper.childNodes);
    this.finalStepSelected = null;

    const answers = [];

    this.questions.forEach((question) => {
      question.answers.forEach((answer) => {
        if (answer.chosen && typeof answer.redirect !== 'string') {
          answers.push(answer.text)
        }
      })
    })

    const combos = this.redirectMap.filter((redirectSet) => intersection(redirectSet.combination, answers).length === answers.length);

    let finalStepHTML = `<div class="skin-quiz__question-header fade-in-KT">${this.finalStep.header}</div>`;
    finalStepHTML += '<div class="skin-quiz__answer-wrapper fade-in-KT">';

    combos.forEach((combo) => {
      combo.answers.forEach((answer) => {
        finalStepHTML += `
            <div class="skin-quiz__answer" data-redirect="${answer.redirect}">
               <div class="skin-quiz__answer-circle"></div>
              <span>${answer.text}</span>
            </div>`;
      });
    });

    finalStepHTML += '</div>';

    this.questionWrapper.innerHTML = finalStepHTML;
  }

  nextButtonAvailabilityControl = () => {
    if (this.currentQuestion.value < this.questions.length) {
      this.nextButton.disabled = !this.questions[this.currentQuestion.value].answers.some((answer) => answer.chosen === true);
    } else {
      this.nextButton.disabled = !this.finalStepSelected;
    }
  }

  buttonsVisibilityControl = () => {
    this.currentQuestion.value === 0
      ? this.prevButton.classList.add('hide')
      : this.prevButton.classList.remove('hide');

    this.nextButton.classList.remove('hide');
  }
}


new SkinQuiz(quizSetting).init();


const slideShow = (slideShowDelaySeconds) => {


  const slideImages = document.querySelectorAll('.skin-quiz .skin-quiz__img img');
  let counter = 0;
  slideImages[counter].classList.remove('hide');

  if (slideImages.length < 1 || !!slideShowDelaySeconds === false) return;

  setInterval(() => {
    slideImages[counter].classList.add('hide');
    counter = counter + 1 === slideImages.length ? 0 : counter + 1;
    slideImages[counter].classList.remove('hide');
  }, slideShowDelaySeconds * 1000)

}
slideShow(slideShowDelaySeconds);
